import { Injectable, inject } from '@angular/core';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import { CmsApiService } from '../api/cms-api.service';
import { LocalHistoryService } from '../local-history.service';
import { ToastMessageService } from '../../shared/modules/toast-message/toast-message.service';
import { LanguageService } from '../language.service';
import { first } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ModalService } from '../../modal/modal.service';

/**
 * Game mode list
 */
export enum GameMode {
  DEMO = 'demo',
  REAL = 'real',
}

@Injectable({
  providedIn: 'root',
})
export class GamesLauncherService {
  private _user = inject(UserService);
  private _language = inject(LanguageService);
  private _router = inject(Router);
  private _cmsApi = inject(CmsApiService);
  private _toast = inject(ToastMessageService);
  private _localHistory = inject(LocalHistoryService);
  private _modal = inject(ModalService);

  /**
   * Is game launched now
   */
  public gameLaunched = false;

  /**
   * Current launched game
   */
  public currentGame: any;

  /**
   * Returns iframe source for provided game
   * @param game
   * @param demo
   */
  public gameSource(game: any, demo: boolean = false): { url: string; mode: GameMode } {
    return {
      url: demo ? game?.demoLaunchURL : game?.launchURL,
      mode: demo ? GameMode.DEMO : GameMode.REAL,
    };
  }

  /**
   * Check if game available for current user
   * @param game
   */
  public checkGameAvailability(game): boolean {
    if (game) {
      if ((!this._user.auth && game.live) || (!this._user.auth && game.nodemo)) {
        this._user.authUser();
      }
      if (this._user.auth && !game?.externalData?.currencies?.includes(this._user.info?.activeWallet?.currency)) {
        this._gameNotAvailablePopup({
          currencyList: game?.externalData?.currencies || [],
        });
        return false;
      }

      return true;
    }
    this._gameNotAvailablePopup();
    return false;
  }

  /**
   * Open random game
   */
  openRandomGame(): Observable<any> {
    return this._cmsApi.gameListNoCache({
      random: 1,
      limit: 1,
    });
  }

  /**
   * Open game by external id
   * @param game
   */
  openGameByExternalId(game: any) {
    this._cmsApi
      .gameList({ 'external_id[]': game.externalId })
      .pipe(first())
      .subscribe(e => {
        if (e.data.gameList) {
          this._router.navigateByUrl(`/${this._language.current}/play/` + e.data.gameList[0].slug, {
            state: {
              game: e.data.gameList[0],
            },
          });
        } else {
          this._toast.warning('This game is not available');
        }
      });
  }

  public getRandomIdxFromGames(additionalGames: any[]): number {
    const indexes = Array.from(Array(additionalGames?.length).keys());
    const availableIndexes = indexes?.filter(index => additionalGames[index]?.source);
    return availableIndexes[Math.floor(Math.random() * availableIndexes?.length)];
  }

  /**
   * Send Game open data for auto top (if user is auth)
   */
  public postGameOpen(gameId: number | string) {
    if (this._user.auth) {
      this._cmsApi
        .postGameOpen(
          {
            user_id: this._user.info.id,
            game: gameId,
          },
          { country_short: this._user.info.country },
        )
        .subscribe();
    }
  }

  /**
   * Show game not available popup
   * @private
   */
  private async _gameNotAvailablePopup(data: object = {}) {
    this._router.navigateByUrl('/games').then(async () => {
      const component = await import('../../modal/components/lazy/game-not-available-modal/game-not-available-modal.component');
      await this._modal.openLazy(component?.GameNotAvailableModalComponent, {
        data,
        closeAfterRouteChange: false,
      });
    });
  }
}
